import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Delete, DragIndicator, Lock, LockOpen } from '@mui/icons-material';
import { Box, Card, CardActionArea, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Circle, Layer, Rect, RegularPolygon, Stage } from 'react-konva';
import useImage from 'use-image';
import { IconButton } from '~/components/IconButton';
import { Tooltip } from '~/components/Tooltip';
import { sortableCollisionDetection, useSortableSensors } from '~/hooks/sortable';
import type { StudioItem } from '../../context';
import { useStudio } from '../../context';
import type { DesignEdit__Design as Design } from '../DesignEdit.generated';

const ITEM_PREVIEW_WIDTH = 75;
const ITEM_PREVIEW_HEIGHT = 40;

const StudioLayerItemText = ({ item }: { item: StudioItem }) => {
  return (
    <Tooltip title={String(item.text)}>
      <Typography variant="subtitle1" noWrap={true}>
        {item.text}
      </Typography>
    </Tooltip>
  );
};

const StudioLayerItemImage = ({
  design,
  item,
}: {
  design: Design | undefined;
  item: StudioItem;
}) => {
  const mediaItem = useMemo(
    () => design?.mediaItems.find((x) => x.id === item.__mediaItemId),
    [design, item.__mediaItemId],
  );

  const src = mediaItem?.thumbnails[0]?.uri ?? '';

  const [image] = useImage(src, 'anonymous');

  if (!src || !image) return null;

  return (
    <Tooltip title={mediaItem?.originalFile.originalFilename}>
      <Box
        alt={mediaItem?.originalFile.originalFilename ?? ''}
        component="img"
        src={src}
        sx={{
          objectFit: 'contain',
          maxWidth: '100%',
          maxHeight: '100%',
          borderRadius: 1,
        }}
      />
    </Tooltip>
  );
};

const StudioLayerItemContent = ({
  design,
  item,
}: {
  design: Design | undefined;
  item: StudioItem;
}) => {
  const itemWidth = item.width ?? ITEM_PREVIEW_WIDTH;
  const itemHeight = item.height ?? ITEM_PREVIEW_HEIGHT;

  const scaleFactor = Math.min(ITEM_PREVIEW_WIDTH / itemWidth, ITEM_PREVIEW_HEIGHT / itemHeight);

  const scaledItem = {
    ...item,
    draggable: false,
    listening: false,
    x: 20, // default, specific types will override where needed
    y: (ITEM_PREVIEW_HEIGHT - itemHeight * scaleFactor) / 2,
    width: itemWidth * scaleFactor,
    height: itemHeight * scaleFactor,
    strokeWidth: (item.strokeWidth ?? 1) * scaleFactor,
    cornerRadius: (item.cornerRadius ?? 0) * scaleFactor,
  };

  const isCanvasType = ['circle', 'rect', 'triangle'].includes(scaledItem.type);

  const isMediaType = ['image', 'video'].includes(scaledItem.type);

  return (
    <Box
      alignItems="center"
      display="flex"
      height={ITEM_PREVIEW_HEIGHT}
      maxHeight={ITEM_PREVIEW_HEIGHT}
      maxWidth={ITEM_PREVIEW_WIDTH}
      width={ITEM_PREVIEW_WIDTH}
    >
      {scaledItem.type === 'text' && <StudioLayerItemText item={scaledItem} />}

      {isMediaType && <StudioLayerItemImage item={scaledItem} design={design} />}

      {isCanvasType && (
        <Stage width={ITEM_PREVIEW_WIDTH} height={ITEM_PREVIEW_HEIGHT}>
          <Layer listening={false}>
            {scaledItem.type === 'circle' && (
              <Circle
                {...scaledItem}
                radius={(itemWidth / 2) * scaleFactor - ((item.strokeWidth ?? 1) * scaleFactor) / 2}
                y={20}
              />
            )}
            {scaledItem.type === 'rect' && <Rect {...scaledItem} x={0} />}
            {scaledItem.type === 'triangle' && (
              <RegularPolygon
                {...scaledItem}
                radius={item.radius * scaleFactor}
                y={item.rotation === 0 ? 24 : 20}
              />
            )}
            {(scaledItem.type === 'image' || scaledItem.type === 'video') && (
              <StudioLayerItemImage item={scaledItem} design={design} />
            )}
          </Layer>
        </Stage>
      )}
    </Box>
  );
};

const SortableLayerItem = ({ design, item }: { design: Design | undefined; item: StudioItem }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { deleteItems, selectItems, selectedIds, toggleLockItems } = useStudio();

  const isSelected = selectedIds.has(item.id);

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !!item.locked });

  // The label only belongs on the handle while dnd-kit makes it a focusable button.
  const dragHandleProps = item.locked
    ? {}
    : {
        ...attributes,
        ...listeners,
        'aria-label': t('pages.studio.edit.layers.reorder_tooltip'),
      };

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
      }}
    >
      <Card
        sx={{
          marginTop: 0.5,
          position: 'relative',
          ...(isSelected && {
            '& .MuiButtonBase-root': { backgroundColor: 'studio.layerHighlight' },
          }),
        }}
        variant="outlined"
      >
        {item.locked && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              zIndex: 1,
              pointerEvents: 'none',
            }}
          />
        )}
        {/* The focusable action area owns the click, so Enter and Space select the layer too.
            It stays a div because it holds the lock and delete buttons; ButtonBase still gives
            it role="button" and key handling, which the lint rules can't see. */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <CardActionArea
          component="div"
          onClick={() => selectItems([item.id])}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Box
              ref={setActivatorNodeRef}
              sx={{
                alignItems: 'flex-start',
                display: 'flex',
                cursor: item.locked ? 'not-allowed' : 'grab',
                ...(item.locked && { opacity: 0.4 }),
                '&:focus-visible': theme.mixins.focusRing,
              }}
              {...dragHandleProps}
            >
              <Tooltip title={t('pages.studio.edit.layers.reorder_tooltip')}>
                <DragIndicator color="secondary" />
              </Tooltip>
            </Box>

            <StudioLayerItemContent design={design} item={item} />
          </Box>

          <Box sx={{ display: 'flex', whiteSpace: 'nowrap' }}>
            <IconButton
              label={
                item.locked
                  ? t('pages.studio.edit.layers.unlock_tooltip')
                  : t('pages.studio.edit.layers.lock_tooltip')
              }
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleLockItems([item.id]);
              }}
            >
              {item.locked ? (
                <Lock sx={{ color: theme.palette.primary.dark }} />
              ) : (
                <LockOpen sx={{ color: theme.palette.primary.dark }} />
              )}
            </IconButton>
            <IconButton
              label={t('pages.studio.edit.layers.delete_tooltip')}
              size="small"
              color={item.locked ? 'default' : 'error'}
              disabled={!!item.locked}
              onClick={(e) => {
                e.stopPropagation();
                deleteItems([item.id]);
              }}
            >
              <Delete />
            </IconButton>
          </Box>
        </CardActionArea>
      </Card>
    </Box>
  );
};

export const StudioLayers = ({ design }: { design: Design | undefined }) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const { items, reorderItems } = useStudio();

  const orderedItems = useMemo(() => {
    return items.slice().reverse();
  }, [items]);

  // Only include unlocked items in sortable context - locked items stay in place
  const sortableIds = useMemo(
    () => orderedItems.filter((item) => !item.locked).map((item) => item.id),
    [orderedItems],
  );

  const sensors = useSortableSensors();

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    // Get unlocked items only for reordering
    const unlockedItems = orderedItems.filter((item) => !item.locked);
    const oldIndex = unlockedItems.findIndex((item) => item.id === active.id);
    const newIndex = unlockedItems.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder only unlocked items
    const newUnlockedItems = [...unlockedItems];
    const [movedItem] = newUnlockedItems.splice(oldIndex, 1);
    newUnlockedItems.splice(newIndex, 0, movedItem);

    // Reconstruct full array with locked items in their original positions
    const lockedPositions = new Map<number, StudioItem>();
    orderedItems.forEach((item, index) => {
      if (item.locked) lockedPositions.set(index, item);
    });

    const newOrderedItems: StudioItem[] = [];
    let unlockedIdx = 0;
    for (let i = 0; i < orderedItems.length; i++) {
      const lockedItem = lockedPositions.get(i);
      if (lockedItem) {
        newOrderedItems.push(lockedItem);
      } else {
        newOrderedItems.push(newUnlockedItems[unlockedIdx++]);
      }
    }

    reorderItems([...newOrderedItems.map((x) => x.id).reverse()]);
  };

  const activeItem = orderedItems.find((item) => item.id === activeId);

  return (
    <Box
      borderLeft="1px solid rgb(63, 63, 63, .15)"
      display="flex"
      flexDirection="column"
      gap={1}
      height="100%"
      sx={{
        backgroundColor: 'common.white',
      }}
    >
      <Stack height="100%">
        <Box
          flexGrow={1}
          paddingBottom={1}
          paddingTop={0.5}
          paddingX={1}
          sx={{ overflowY: 'auto' }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={sortableCollisionDetection}
            modifiers={[restrictToVerticalAxis]}
            onDragStart={(e) => setActiveId(String(e.active.id))}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveId(null)}
          >
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              <Box>
                {orderedItems.map((item) => (
                  <SortableLayerItem design={design} item={item} key={item.id} />
                ))}
              </Box>
            </SortableContext>

            <DragOverlay dropAnimation={null}>
              {activeItem && (
                <Card variant="outlined" sx={{ opacity: 0.8, boxShadow: 3 }}>
                  <CardActionArea
                    component="div"
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 0.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DragIndicator color="secondary" />
                      <StudioLayerItemContent design={design} item={activeItem} />
                    </Box>
                  </CardActionArea>
                </Card>
              )}
            </DragOverlay>
          </DndContext>
        </Box>
      </Stack>
    </Box>
  );
};
